// frontend/src/components/chat/MatchResultsCard.tsx
//
// READ-ONLY matching card. It renders the receipt draft (edited in ReceiptDrawer),
// runs the auto-routing logic, and owns the disposition decision. All editing
// happens in the drawer ("Review / edit"); the card never mutates the draft.

import React, { useState } from 'react';
import { CATEGORY_META, isRegulatory, type JobCategory } from '../../data/docTypes';
import { computeRouting, computeRegulatoryRouting, orderedFor, type ReceiptDraft } from '../../data/receiptDraft';

const LBL = 'text-xs font-semibold text-slate-400 uppercase tracking-wider';

function CheckIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function RO({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <span className={LBL}>{label}</span>
      <div className="text-sm text-slate-800 font-medium leading-tight mt-0.5">{value || '—'}</div>
    </div>
  );
}

export function MatchResultsCard({
  draft,
  onConfirm,
  onReceivedShort,
  onNoJobFound,
  onHold,
  onReviewEdit,
  onViewPdf,
  onOpenAllocation,
}: any) {
  const [reviewed, setReviewed] = useState(false);
  const [allocationReviewed, setAllocationReviewed] = useState(false);
  const [showHold, setShowHold] = useState(false);
  const [holdReason, setHoldReason] = useState('');

  if (!draft) return null;
  const d = draft as ReceiptDraft;
  const cat = d.category as JobCategory;
  const reg = isRegulatory(cat);
  const meta = CATEGORY_META[cat];
  const multiJob = !reg && d.jobs.length > 1;
  const routing = reg ? computeRegulatoryRouting(d) : computeRouting(d);
  const hasJob = routing.disposition !== 'no_job_found';

  const confirmReady = reviewed && hasJob && (!multiJob || allocationReviewed);
  const jobNumbers = d.jobs.map(j => j.jobNumber).filter(Boolean);

  const isSuggested = (x: string) => routing.disposition === x;
  const routeToCsSuggested = reg && isSuggested('route_to_cs');
  const primaryBtn = 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:shadow-lg hover:shadow-teal-500/30';
  const outlineBtn = 'border border-slate-300 text-slate-600 hover:bg-white hover:border-slate-400';
  const ring = 'ring-2 ring-offset-1 ring-teal-400';

  const review = () => { setReviewed(true); onReviewEdit?.(); };

  return (
    <div className="match-results-card rounded-xl shadow-lg border border-slate-200 overflow-hidden bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-sm font-semibold text-white whitespace-nowrap">Match review</span>
          <span className={`${meta.tp} !text-[11px]`}>{meta.label}</span>
          <span className="text-xs text-slate-400 truncate">— {d.filename}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {d.fileUri && onViewPdf && (
            <button onClick={() => onViewPdf({ fileUri: d.fileUri, scan: d.scan })} title="View scanned PDF"
              className="flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium border-teal-400/60 text-teal-400 hover:bg-white/10 hover:text-teal-300">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
              PDF
            </button>
          )}
          <button onClick={review}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-semibold ${reviewed ? 'border-teal-400 bg-teal-400/20 text-teal-200' : 'border-teal-400 bg-teal-400/10 text-teal-300 hover:bg-teal-400/20'}`}>
            {reviewed && <CheckIcon />} Review / edit
          </button>
          {multiJob && onOpenAllocation && (
            <button onClick={() => { onOpenAllocation(); setAllocationReviewed(true); }}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium ${allocationReviewed ? 'border-teal-400 bg-teal-400/20 text-teal-300' : 'border-teal-400/60 text-teal-400 hover:bg-white/10'}`}>
              {allocationReviewed && <CheckIcon />} Allocate
            </button>
          )}
        </div>
      </div>

      {/* Body — read-only */}
      <div className="px-4 py-3 space-y-3">
        <div className="grid grid-cols-2 gap-3 pb-2 border-b border-slate-100">
          <RO label="Client name(s)" value={d.clientNames.join(' / ')} />
          <RO label="Job #(s)" value={jobNumbers.join(', ')} />
          {reg ? (
            <>
              <RO label="Book name" value={d.bookName} />
              <RO label="Material type" value={d.materialType} />
              <RO label="Lit code" value={d.litCode} />
              <RO label="Cusip / ticker" value={[d.cusip, d.ticker].filter(Boolean).join(' / ')} />
              <RO label="Post-sale" value={d.postSale ? 'Post-Sale' : 'Interim'} />
              <RO label="Quantity" value={d.quantity} />
            </>
          ) : (
            <>
              <RO label="Event date" value={d.eventDate} />
              <RO label="Record date" value={d.recordDate} />
            </>
          )}
        </div>

        {/* Documents — received vs ordered (read-only) */}
        <div>
          <div className="grid gap-2 py-1 border-b-2 border-slate-300 mb-1" style={{ gridTemplateColumns: '1fr 70px 70px' }}>
            <span className={LBL}>Document type</span>
            <span className={`${LBL} text-right`}>Received</span>
            <span className={`${LBL} text-right`}>Ordered</span>
          </div>
          {d.receivedDocs.map((doc, i) => {
            const ord = orderedFor(d, doc.type);
            const mismatch = doc.qty !== ord || !doc.type;
            return (
              <div key={i} className="grid gap-2 py-1 items-center border-b border-slate-100 last:border-0" style={{ gridTemplateColumns: '1fr 70px 70px' }}>
                <span className="text-sm text-slate-700 truncate">{doc.type || '— none —'}</span>
                <span className={`text-sm text-right font-semibold ${mismatch ? 'text-amber-600' : 'text-emerald-600'}`}>{doc.qty}</span>
                <span className="text-sm text-right text-slate-500">{ord}</span>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-1 text-xs text-slate-400">
          <span>Extracted scan · {d.scan}</span>
          <span className="italic">Page-to-page comparison — deactivated</span>
        </div>
      </div>

      {/* Auto-detected routing */}
      <div className={`mx-4 mb-2 px-3 py-2 rounded-lg border text-sm flex items-center gap-2 ${routing.cls}`}>
        <span className="font-semibold uppercase text-xs tracking-wide">Suggested</span>
        {routing.text}
      </div>

      {/* Review gate */}
      {!confirmReady && (
        <div className="mx-4 mb-2 px-3 py-2 bg-slate-100 rounded-lg flex items-center gap-4 text-xs text-slate-500">
          <span className="font-medium text-slate-600">To confirm:</span>
          <span className="flex items-center gap-1">{reviewed ? <CheckIcon /> : <span className="text-slate-400">○</span>} Review / edit</span>
          {multiJob && <span className="flex items-center gap-1">{allocationReviewed ? <CheckIcon /> : <span className="text-slate-400">○</span>} Allocation</span>}
          {!hasJob && <span className="flex items-center gap-1 text-red-600"><span className="text-red-400">○</span> Job # required</span>}
        </div>
      )}

      {/* On Hold reason */}
      {showHold && (
        <div className="mx-4 mb-2">
          <textarea className="w-full px-2 py-1 rounded-md border border-slate-200 text-sm resize-none outline-none focus:border-teal-400" rows={2} autoFocus placeholder="Reason for hold…" value={holdReason} onChange={e => setHoldReason(e.target.value)} />
        </div>
      )}

      {/* Dispositions */}
      <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 grid grid-cols-2 gap-2">
        <button disabled={!confirmReady} onClick={() => confirmReady && onConfirm?.()}
          title={!hasJob ? 'Enter a Job # in Review / edit, or choose No Job Found instead.' : undefined}
          className={`py-2.5 px-3 rounded-lg text-sm font-semibold transition-all ${confirmReady ? primaryBtn : 'bg-teal-500/40 text-white/60 cursor-not-allowed'} ${confirmReady && isSuggested('matched') ? ring : ''}`}>
          Mark as Received
        </button>
        <button onClick={() => onReceivedShort?.()}
          className={`py-2.5 px-3 rounded-lg text-xs font-medium border ${reg ? 'border-blue-400 text-blue-600 hover:bg-blue-50' : 'border-amber-400 text-amber-600 hover:bg-amber-50'} ${isSuggested('received_short') ? ring : ''}`}>
          Received Short
        </button>
        <button onClick={() => onNoJobFound?.()}
          className={`py-2.5 px-3 rounded-lg text-xs font-medium ${outlineBtn} ${isSuggested('no_job_found') ? ring : ''}`}>
          No Job Found
        </button>
        <button onClick={() => { if (!showHold) { setShowHold(true); if (routeToCsSuggested) setHoldReason('Quantity is 200 or more, so it was routed to CS (CS-X).'); return; } onHold?.(holdReason); }}
          className={`py-2.5 px-3 rounded-lg text-xs font-medium border border-orange-300 text-orange-600 hover:bg-orange-50 ${routeToCsSuggested ? ring : ''}`}>
          On Hold
        </button>
      </div>
    </div>
  );
}
